package com.taocent.simple.compose.component.richtext.core.internal.paragraph

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.TextRange
import androidx.compose.ui.text.style.TextAlign
import com.taocent.simple.compose.component.richtext.core.internal.format.ParagraphStyleUtils

internal data class RichParagraphModel(
    var textAlign: TextAlign = TextAlign.Left
)

internal class ParagraphModelManager(
    annotated: AnnotatedString
) {
    var models by mutableStateOf(buildModels(annotated))
        private set

    fun restoreFromAnnotated(annotated: AnnotatedString) {
        models = buildModels(annotated)
    }

    fun restore(models: List<RichParagraphModel>) {
        this.models = models.map { it.copy() }
    }

    fun snapshot(): List<RichParagraphModel> {
        return models.map { it.copy() }
    }

    fun paragraphIndexAt(text: String, offset: Int): Int {
        if (text.isEmpty()) return 0
        val pos = offset.coerceIn(0, text.length)
        var index = 0
        var start = 0
        while (start <= text.length) {
            val lineBreak = text.indexOf('\n', start)
            val end = if (lineBreak < 0) text.length else lineBreak + 1
            if (pos < end || lineBreak < 0) return index.coerceAtMost(models.lastIndex.coerceAtLeast(0))
            index++
            start = lineBreak + 1
        }
        return index.coerceAtMost(models.lastIndex.coerceAtLeast(0))
    }

    fun setTextAlign(
        annotated: AnnotatedString,
        selection: TextRange,
        align: TextAlign
    ): AnnotatedString {
        ensureFor(annotated)
        val indexes = paragraphIndexesForSelection(annotated.text, selection)
        for (index in indexes) {
            models[index].textAlign = ParagraphStyleUtils.normalizedTextAlign(align)
        }
        models = models.map { it.copy() }
        // setTextAlign 路径:annotated 自带的 paragraphStyles 是"旧状态",会覆盖用户新设的 align。
        // 临时移除后再 applyTo,确保 applyParagraphTextAligns 用 models(=aligns 数组) 覆盖。
        // paste 路径不走这里,paste 内容的 paragraphStyles 应保留(在 TextEditor.insertAnnotatedString 中处理)。
        val stripped = stripParagraphStyles(annotated)
        return applyTo(stripped)
    }

    /**
     * iOS 平台专有入口:只更新内存中的 paragraph models,**不**重建 annotated 也不写回
     * state.textFieldValue.annotatedString。
     *
     * 背景:iOS 平台 BasicTextField 内部用 UITextField,value.annotatedString 一旦变化
     * (哪怕只是 paragraph styles 变),Compose 会调 UITextField.setAttributedText,
     * 部分 iOS 版本 / 第三方 IME(尤其在 IME 已处于 composing 状态时)对 setAttributedText
     * 敏感,会 reset 候选词面板的 candidate view,表现为"切样式之后输入 IME 候选词不展示"。
     *
     * 与 [setTextAlign] 不同的是,本方法不写回 annotated,BasicTextField 收到的 value
     * 没有变化,UITextField 不会重新调用 setAttributedText,IME 输入会话保持;
     * 下次 onValueChange / syncParagraphModelsAfterTextChange 流程会调 [applyTo],
     * 把 models 同步到 value.annotatedString.paragraphStyles,实现延迟 apply。
     *
     * Android / Desktop 平台仍走 [setTextAlign] 走 default 路径(立即 visual update),
     * 不受此方法影响。
     */
    fun updateTextAlignModels(
        text: String,
        selection: TextRange,
        align: TextAlign
    ) {
        ensureForAnnotatedText(text)
        val indexes = paragraphIndexesForSelection(text, selection)
        for (index in indexes) {
            models[index].textAlign = ParagraphStyleUtils.normalizedTextAlign(align)
        }
        models = models.map { it.copy() }
    }

    private fun ensureForAnnotatedText(text: String) {
        val count = ParagraphStyleUtils.paragraphRanges(text).size
        if (models.size == count) return
        // 调整 models 数量,保留现有 align 状态;新增段落默认 Left。
        // 这样不会丢失用户已经设过的对齐(由 applyTo 写入的 custom paragraph styles)。
        val result = models.toMutableList()
        while (result.size < count) result.add(RichParagraphModel(TextAlign.Left))
        while (result.size > count) result.removeAt(result.lastIndex)
        models = result
    }

    internal fun stripParagraphStyles(annotated: AnnotatedString): AnnotatedString {
        if (annotated.paragraphStyles.isEmpty()) return annotated
        val builder = AnnotatedString.Builder(annotated.length)
        builder.append(annotated.text)
        for (span in annotated.spanStyles) {
            builder.addStyle(span.item, span.start, span.end)
        }
        for (annotation in annotated.getStringAnnotations(0, annotated.length)) {
            builder.addStringAnnotation(annotation.tag, annotation.item, annotation.start, annotation.end)
        }
        return builder.toAnnotatedString()
    }

    /**
     * stripParagraphStyles 后 applyTo，确保 models 为对齐权威源。
     * 用于 [RichTextState.setParagraphTextAlignsLazy] — iOS 懒路径更新 models 后
     * 需立即将新对齐写到 annotatedString，避免旧 paragraphStyles 覆盖 models 新值。
     */
    internal fun stripAndApplyParagraphModels(annotated: AnnotatedString): AnnotatedString {
        return applyTo(stripParagraphStyles(annotated))
    }

    fun textAlignForSelection(
        annotated: AnnotatedString,
        selection: TextRange
    ): TextAlign {
        ensureFor(annotated)
        if (models.isEmpty()) return TextAlign.Left
        val indexes = paragraphIndexesForSelection(annotated.text, selection)
        val first = models[indexes.first()].textAlign
        return if (indexes.all { models[it].textAlign == first }) first else TextAlign.Left
    }

    fun syncAfterTextChange(
        oldText: String,
        newAnnotated: AnnotatedString,
        editStart: Int,
        removedCount: Int,
        insertedText: String,
        fallbackTextAlign: TextAlign? = null
    ): AnnotatedString {
        val oldModels = models.ifEmpty { buildModels(newAnnotated) }
        if (canKeepParagraphModels(oldText, editStart, removedCount, insertedText)) {
            if (models.isEmpty()) {
                models = oldModels
            }
            return applyTo(newAnnotated)
        }
        models = buildEditedModels(
            oldText = oldText,
            oldModels = oldModels,
            newText = newAnnotated.text,
            editStart = editStart,
            removedCount = removedCount,
            insertedText = insertedText,
            fallbackTextAlign = fallbackTextAlign
        )
        return applyTo(newAnnotated)
    }

    private fun canKeepParagraphModels(
        oldText: String,
        editStart: Int,
        removedCount: Int,
        insertedText: String
    ): Boolean {
        if (insertedText.any { it == '\n' }) return false
        if (removedCount <= 0) return true
        val start = editStart.coerceIn(0, oldText.length)
        val end = (editStart + removedCount).coerceIn(start, oldText.length)
        return oldText.indexOf('\n', start).let { it < 0 || it >= end }
    }

    fun applyTo(annotated: AnnotatedString): AnnotatedString {
        val applied = ParagraphStyleUtils.applyParagraphTextAligns(annotated, models.map { it.textAlign })
        // 同步把 models 内部各段 align 更新成最终生效的值(annotated 自带 > models 旧值)
        // 修复前:models 不会随 paste/插入文本被 paste 内容更新,导致
        //        target.textAlignForSelection() / currentTextAlign 仍是 target 旧 align,
        //        工具栏对齐按钮的"选中"状态错位。
        syncModelsFromApplied(applied)
        return applied
    }

    private fun syncModelsFromApplied(applied: AnnotatedString) {
        val text = applied.text
        val ranges = ParagraphStyleUtils.paragraphRanges(text)
        if (ranges.isEmpty()) return
        val result = models.toMutableList()
        // 调整 models 数量与新文本的段数一致
        while (result.size < ranges.size) result.add(RichParagraphModel(TextAlign.Left))
        while (result.size > ranges.size) result.removeAt(result.lastIndex)
        for ((index, range) in ranges.withIndex()) {
            // 判定"非内容段": 段内 0 字符 或 段首是 '\n'(即中间空行 / 末尾虚拟空行)。
            // 这类段 textAlignAt 会退到前一段(空行) 或返回 Left(末尾虚拟),
            // 用它会覆盖该段已有的、由 setTextAlign / insertAnnotatedString 设定的 align。
            // 正确做法:沿用 models 自己的值,不重写。
            val isEmptyLine = text.isEmpty() ||
                range.start == range.end ||
                (range.start < text.length && text[range.start] == '\n')
            if (isEmptyLine) continue
            val align = ParagraphStyleUtils.textAlignAt(applied, range.start)
            result[index].textAlign = align
        }
        models = result.map { it.copy() }
    }

    private fun ensureFor(annotated: AnnotatedString) {
        val count = ParagraphStyleUtils.paragraphRanges(annotated.text).size
        if (models.size == count) return
        models = buildModels(annotated)
    }

    private fun buildModels(annotated: AnnotatedString): List<RichParagraphModel> {
        return ParagraphStyleUtils.paragraphRanges(annotated.text).map { range ->
            RichParagraphModel(ParagraphStyleUtils.textAlignAt(annotated, range.start))
        }
    }

    private fun paragraphIndexesForSelection(text: String, selection: TextRange): List<Int> {
        if (models.isEmpty()) return listOf(0)
        if (selection.collapsed) return listOf(paragraphIndexAt(text, selection.start))
        val ranges = ParagraphStyleUtils.lineRangesForSelection(text, selection)
        return ranges.map { range -> paragraphIndexAt(text, range.start) }.distinct().ifEmpty {
            listOf(paragraphIndexAt(text, selection.min))
        }
    }

    private fun buildEditedModels(
        oldText: String,
        oldModels: List<RichParagraphModel>,
        newText: String,
        editStart: Int,
        removedCount: Int,
        insertedText: String,
        fallbackTextAlign: TextAlign?
    ): List<RichParagraphModel> {
        val newCount = ParagraphStyleUtils.paragraphRanges(newText).size
        val insertedNewlines = insertedText.count { it == '\n' }
        if (insertedNewlines > 0) {
            val baseIndex = paragraphIndexAt(oldText, editStart).coerceIn(0, oldModels.lastIndex.coerceAtLeast(0))
            val baseAlign = ParagraphStyleUtils.normalizedTextAlign(fallbackTextAlign ?: oldModels.getOrNull(baseIndex)?.textAlign ?: TextAlign.Left)
            val result = oldModels.map { it.copy() }.toMutableList()
            repeat(insertedNewlines) {
                result.add(baseIndex + 1, RichParagraphModel(baseAlign))
            }
            return fitCount(result, newCount, baseAlign)
        }
        if (removedCount > 0) {
            val startIndex = paragraphIndexAt(oldText, editStart).coerceIn(0, oldModels.lastIndex.coerceAtLeast(0))
            val endIndex = paragraphIndexAt(oldText, editStart + removedCount).coerceIn(0, oldModels.lastIndex.coerceAtLeast(0))
            val result = oldModels.map { it.copy() }.toMutableList()
            if (endIndex > startIndex) {
                for (index in endIndex downTo startIndex + 1) {
                    if (index in result.indices) result.removeAt(index)
                }
            }
            return fitCount(result, newCount, oldModels.getOrNull(startIndex)?.textAlign ?: TextAlign.Left)
        }
        val align = fallbackTextAlign ?: oldModels.getOrNull(paragraphIndexAt(oldText, editStart))?.textAlign ?: TextAlign.Left
        return fitCount(oldModels.map { it.copy() }, newCount, align)
    }

    private fun fitCount(
        models: List<RichParagraphModel>,
        count: Int,
        align: TextAlign
    ): List<RichParagraphModel> {
        val result = models.toMutableList()
        while (result.size > count) result.removeAt(result.lastIndex)
        while (result.size < count) result.add(RichParagraphModel(ParagraphStyleUtils.normalizedTextAlign(align)))
        if (result.isEmpty()) result.add(RichParagraphModel(TextAlign.Left))
        return result
    }
}
